import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocket, DATA_REFRESH_EVENT } from '../../contexts/WebSocketContext';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Users, GraduationCap, BookOpen, Calendar, Bell, MessageSquare,
  TrendingUp, AlertTriangle, Video, Clock, Plus, ChevronRight,
  Building2, CheckCircle, XCircle, Megaphone, FileText, Settings,
  BarChart3, Target, RefreshCcw, ExternalLink, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const CfaAdminDashboard = ({ onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount } = useWebSocket();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      // Cache-bust so Actualiser always hits the API (no stale browser/proxy cache)
      const response = await api.get('/dashboard/cfa-admin/operational', {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setData(response.data);
      if (manual) {
        toast.success('Tableau de bord actualisé');
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Listen for real-time data refresh events
  useEffect(() => {
    const handleDataRefresh = () => {
      fetchDashboardData();
    };

    window.addEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
    return () => window.removeEventListener(DATA_REFRESH_EVENT, handleDataRefresh);
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Centre de Contrôle" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const alerts = data?.alerts || {};
  const upcomingSessions = data?.upcoming_sessions || [];
  const recentActivity = data?.recent_activity || [];
  const announcements = data?.announcements || [];

  // KPI Cards configuration
  const kpiCards = [
    {
      title: 'Étudiants Actifs',
      value: kpis.active_students || 0,
      subtitle: `sur ${kpis.total_students || 0} inscrits`,
      icon: Users,
      color: 'text-violet-600',
      bg: 'bg-violet-50 dark:bg-violet-900/20',
      onClick: () => navigate('/students')
    },
    {
      title: 'Formateurs',
      value: kpis.active_trainers || 0,
      subtitle: 'actifs',
      icon: GraduationCap,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      onClick: () => navigate('/trainers')
    },
    {
      title: 'Cours',
      value: kpis.total_courses || 0,
      subtitle: 'disponibles',
      icon: BookOpen,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      onClick: () => navigate('/courses')
    },
    {
      title: 'Taux Complétion',
      value: `${kpis.completion_rate || 0}%`,
      subtitle: `Réussite: ${kpis.success_rate || 0}%`,
      icon: TrendingUp,
      color: 'text-amber-600',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      onClick: null
    },
  ];

  // Alert Cards configuration
  const alertCards = [
    {
      title: 'Sessions Aujourd\'hui',
      value: alerts.sessions_today || 0,
      icon: Video,
      color: alerts.sessions_today > 0 ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-slate-50',
      textColor: alerts.sessions_today > 0 ? 'text-emerald-700' : 'text-slate-500',
      onClick: () => navigate('/admin/live-sessions')
    },
    {
      title: 'Sessions (7j)',
      value: alerts.upcoming_sessions_week || 0,
      icon: Calendar,
      color: 'border-blue-500 bg-blue-50',
      textColor: 'text-blue-700',
      onClick: () => navigate('/admin/live-sessions')
    },
    {
      title: 'À Corriger',
      value: alerts.pending_corrections || 0,
      icon: FileText,
      color: alerts.pending_corrections > 0 ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-slate-50',
      textColor: alerts.pending_corrections > 0 ? 'text-amber-700' : 'text-slate-500',
      onClick: () => navigate('/trainer/evaluations')
    },
    {
      title: 'Sans Formateur',
      value: alerts.sessions_without_trainer || 0,
      icon: AlertTriangle,
      color: alerts.sessions_without_trainer > 0 ? 'border-red-500 bg-red-50' : 'border-slate-200 bg-slate-50',
      textColor: alerts.sessions_without_trainer > 0 ? 'text-red-700' : 'text-slate-500',
      onClick: () => navigate('/admin/live-sessions')
    },
  ];

  // Quick Actions
  const quickActions = [
    { icon: Users, label: 'Ajouter Étudiant', path: '/students?action=create', color: 'bg-violet-600 hover:bg-violet-700' },
    { icon: GraduationCap, label: 'Ajouter Formateur', path: '/trainers?action=create', color: 'bg-emerald-600 hover:bg-emerald-700' },
    { icon: Video, label: 'Planifier Session', path: '/admin/live-sessions?action=create', color: 'bg-blue-600 hover:bg-blue-700' },
    { icon: Megaphone, label: 'Envoyer Annonce', path: '/admin/announcements?action=create', color: 'bg-amber-600 hover:bg-amber-700' },
  ];

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Non planifié';
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    
    if (hours < 1) return 'Il y a quelques minutes';
    if (hours < 24) return `Il y a ${hours}h`;
    if (hours < 48) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'lesson_completed': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'quiz_passed': return <Target className="w-4 h-4 text-emerald-500" />;
      case 'quiz_failed': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'homework_submitted': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'login': return <Users className="w-4 h-4 text-violet-500" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Centre de Contrôle" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header avec info organisation */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {data?.organization?.name || 'Mon Centre'}
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Bonjour {user?.first_name}, bienvenue dans votre espace de gestion
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={refreshing}
              onClick={() => fetchDashboardData({ manual: true })}
              data-testid="refresh-dashboard-btn"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Actualisation…' : 'Actualiser'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/admin/settings')} data-testid="settings-btn">
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpiCards.map((kpi, index) => {
            const Icon = kpi.icon;
            return (
              <Card 
                key={index} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${kpi.onClick ? 'hover:border-violet-300' : ''}`}
                onClick={kpi.onClick}
                data-testid={`kpi-card-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${kpi.color}`} />
                    </div>
                    {kpi.onClick && <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{kpi.value}</p>
                  <p className="text-xs text-slate-500">{kpi.title}</p>
                  <p className="text-xs text-slate-400 mt-1">{kpi.subtitle}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Alertes nécessitant action */}
        <Card className="mb-6" data-testid="alerts-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Alertes & Actions Requises
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {alertCards.map((alert, index) => {
                const Icon = alert.icon;
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${alert.color} cursor-pointer hover:opacity-80 transition-opacity`}
                    onClick={alert.onClick}
                    data-testid={`alert-card-${index}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className={`w-4 h-4 ${alert.textColor}`} />
                      <span className={`text-xs font-medium ${alert.textColor}`}>{alert.title}</span>
                    </div>
                    <p className={`text-2xl font-bold ${alert.textColor}`}>{alert.value}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="mb-6" data-testid="quick-actions-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-5 h-5 text-violet-600" />
              Actions Rapides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={index}
                    className={`h-auto py-4 flex flex-col items-center gap-2 ${action.color} text-white`}
                    onClick={() => navigate(action.path)}
                    data-testid={`quick-action-${index}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sessions Live à venir */}
          <Card className="lg:col-span-2" data-testid="upcoming-sessions-section">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Video className="w-5 h-5 text-emerald-600" />
                  Sessions Live à Venir
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/planning')}>
                  Voir Planning
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[320px]">
                {upcomingSessions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Video className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Aucune session planifiée</p>
                    <Button variant="link" size="sm" onClick={() => navigate('/admin/planning')}>
                      Planifier une session
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upcomingSessions.map((session) => (
                      <div
                        key={session.id}
                        className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        data-testid={`session-${session.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-900 dark:text-white truncate">
                                {session.title}
                              </h4>
                              <Badge 
                                className={session.status === 'live' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-600'}
                              >
                                {session.status === 'live' ? 'En direct' : 'Planifié'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                              {session.course?.name}
                            </p>
                            <div className="flex items-center gap-4 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDateTime(session.scheduled_at)}
                              </span>
                              {session.duration_minutes && (
                                <span>{session.duration_minutes} min</span>
                              )}
                            </div>
                            <div className="mt-2">
                              {session.trainer ? (
                                <span className="text-xs text-slate-600 flex items-center gap-1">
                                  <GraduationCap className="w-3 h-3" />
                                  {session.trainer.name}
                                </span>
                              ) : (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Sans formateur
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            {session.live_url && (
                              <Button size="sm" variant="outline" asChild>
                                <a href={session.live_url} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-3 h-3 mr-1" />
                                  Lien
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Sidebar: Activité + Annonces */}
          <div className="space-y-6">
            {/* Activité Récente */}
            <Card data-testid="recent-activity-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Activité Récente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[180px]">
                  {recentActivity.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Aucune activité récente</p>
                  ) : (
                    <div className="space-y-3">
                      {recentActivity.slice(0, 5).map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3">
                          {getActivityIcon(activity.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-900 dark:text-white truncate">
                              {activity.student_name}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{activity.description}</p>
                          </div>
                          <span className="text-xs text-slate-400 whitespace-nowrap">
                            {formatTimeAgo(activity.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Annonces */}
            <Card data-testid="announcements-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-purple-600" />
                    Annonces
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/announcements')}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[180px]">
                  {announcements.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-slate-500">Aucune annonce</p>
                      <Button variant="link" size="sm" onClick={() => navigate('/admin/announcements/new')}>
                        Créer une annonce
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {announcements.map((announcement) => (
                        <div key={announcement.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                          <div className="flex items-start gap-2 mb-1">
                            <h4 className="text-sm font-medium text-slate-900 dark:text-white flex-1 truncate">
                              {announcement.title}
                            </h4>
                            {announcement.priority === 'urgent' && (
                              <Badge variant="destructive" className="text-xs">Urgent</Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{announcement.content}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(announcement.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CfaAdminDashboard;
